import type { ValueOf } from "next/dist/shared/lib/constants";

export const SYSTEM_ATTRIBUTE_PROPERTY = {
  COMPANY_NAME: "company_name",
  COMPANY_LOGO: "company_logo",
  COMPANY_DOMAIN: "company_domain",
  COMPANY_INDUSTRY: "company_industry",
  COMPANY_TAGS: "company_tags",
  COMPANY_LINKEDIN: "company_linkedin",
  COMPANY_TWITTER: "company_twitter",
  COMPANY_TWITTER_FOLLOWERS: "company_twitter_followers",
  COMPANY_DESCRIPTION: "company_description",
  COMPANY_PRIMARY_LOCATION: "company_primary_location",
  COMPANY_FOUNDED: "company_founded",
  COMPANY_ESTIMATED_REVENUE: "company_estimated_revenue",
  COMPANY_TOTAL_FUND_RAISED: "company_total_fund_raised",
  COMPANY_EMPLOYEE_RANGE: "company_employee_range",
  COMPANY_TYPE: "company_type",

  PEOPLE_FIRST_NAME: "people_first_name",
  PEOPLE_LAST_NAME: "people_last_name",
  PEOPLE_NAME: "people_name",
  PEOPLE_EMAIL: "people_email",
  PEOPLE_EMAIL_VERIFIED: "people_email_verified",
  PEOPLE_AVATAR_URL: "people_avatar_url",
  PEOPLE_JOB_TITLE: "people_job_title",
  PEOPLE_SENIORITY: "people_seniority",
  PEOPLE_LINKEDIN: "people_linkedin",
  PEOPLE_TWITTER: "people_twitter",
  PEOPLE_TWITTER_FOLLOWERS: "people_twitter_followers",
  PEOPLE_PHONE: "people_phone",
  PEOPLE_CURRENT_COMPANY: "people_current_company",
  PEOPLE_OTHER_EMAILS: "people_other_emails",
  PEOPLE_TYPE: "people_type",

  ORG_TEAM_MEMBERS: "org_team_members",

  OPPORTUNITY_NAME: "opportunity_name",
  OPPORTUNITY_ACCOUNT: "opportunity_account",
  OPPORTUNITY_CONTACT: "opportunity_contact",
  OPPORTUNITY_OTHER_STAKEHOLDERS: "opportunity_other_stakeholders",
  OPPORTUNITY_STAGE: "opportunity_stage",
  OPPORTUNITY_NEXT_STEP: "opportunity_next_step",
  OPPORTUNITY_PROBABILITY: "opportunity_probability",
  OPPORTUNITY_CLOSE_DATE: "opportunity_close_date",
  OPPORTUNITY_DEAL_SIZE: "opportunity_deal_size",
  OPPORTUNITY_OWNER: "opportunity_owner",
  OPPORTUNITY_NEXT_ACTION_DATE: "opportunity_next_action_date",
  OPPORTUNITY_CREATION_DATE: "opportunity_creation_date",

  VISITOR_COMPANY_DOMAIN: "visitor_company_domain",
  VISITOR_COMPANY_INTENT: "visitor_company_intent",
  VISITOR_LAST_ACTIVE: "visitor_last_active",
  VISITOR_COUNT: "visitor_count",
  VISITOR_VISIT_COUNT: "visitor_visit_count",
};

export const ATTRIBUTE_ENTITY = {
  COMPANY: "company",
  PEOPLE: "people",
  CUSTOM: "custom",
  OPPORTUNITY: "opportunity",
  TASK: "task",
  NOTE: "note",
  VISITOR_COMPANY: "visitor_company",
  LEAD: "lead",
} as const;

export const ATTRIBUTE_TYPE = {
  STRING: "string",
  NUMBER: "number",
  CHECKBOX: "checkbox",
  DATE: "date",
  DATE_RANGE: "date_range",
  RATING: "rating",
  TIMESTAMP: "timestamp",
  SELECT: "select",
  MULTI_SELECT: "multi_select",
  CURRENCY: "currency",
  PERCENTAGE: "percentage",
  LOCATION: "location",
  TIME: "time",
  DATETIME: "datetime",
  EMAIL: "email",
  PHONE: "phone",
  WEBSITE: "website",
  URL: "url",
  RADIO: "radio",
  TEAM_MEMBER: "team_member",
} as const;

export const ATTRIBUTE_LABEL_MAP: Record<
  ValueOf<typeof ATTRIBUTE_TYPE>,
  string
> = {
  checkbox: "Checkbox",
  currency: "Currency",
  date: "Date",
  date_range: "Date range",
  location: "Location",
  multi_select: "Multi Select",
  number: "Number",
  rating: "Rating",
  select: "Dropdown",
  string: "Text",
  timestamp: "Timestamp",
  datetime: "Date & Time",
  time: "Time",
  email: "Email",
  phone: "Phone",
  radio: "Radio",
  url: "URL",
  website: "Website",
  team_member: "Team Members",
  percentage: "Percentage",
};

export const CUSTOM_LIST_VIEW_TYPES = {
  KANBAN: "kanban",
  TABLE: "table",
} as const;

export const SYSTEM_ATTRIBUTES_TO_ADD = [
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_NAME,
    label: "Company name",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "company name",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_DOMAIN,
    label: "Website",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "domain website",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_ESTIMATED_REVENUE,
    label: "Est. ARR",
    attributeType: ATTRIBUTE_TYPE.NUMBER,
    keyword: "revenue arr",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_TOTAL_FUND_RAISED,
    label: "Total fundings",
    attributeType: ATTRIBUTE_TYPE.NUMBER,
    keyword: "fundings",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_DESCRIPTION,
    label: "Company description",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "company description",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_LINKEDIN,
    label: "Company linkedIn",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "company linkedin",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_INDUSTRY,
    label: "Industry",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "industry",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_TWITTER,
    label: "Company twitter",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "company twitter",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_EMPLOYEE_RANGE,
    label: "Employee range",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "employee range",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_FOUNDED,
    label: "Founded",
    attributeType: ATTRIBUTE_TYPE.DATE,
    keyword: "founded",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_PRIMARY_LOCATION,
    label: "Company location",
    attributeType: ATTRIBUTE_TYPE.LOCATION,
    keyword: "comapany location address",
  },
  {
    entity: ATTRIBUTE_ENTITY.COMPANY,
    property: SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_TWITTER_FOLLOWERS,
    label: "Twitter followers",
    attributeType: ATTRIBUTE_TYPE.NUMBER,
    keyword: "twitter followers",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_FIRST_NAME,
    label: "First name",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "first name",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_LAST_NAME,
    label: "Last name",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "last name",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_NAME,
    label: "Full name",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "full name",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_JOB_TITLE,
    label: "Job title",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "job title",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_EMAIL,
    label: "Email",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "email",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_LINKEDIN,
    label: "Contact linkedIn",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "contact linkedin",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_TWITTER,
    label: "Contact twitter",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "contact twitter",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_TWITTER_FOLLOWERS,
    label: "Twitter followers",
    attributeType: ATTRIBUTE_TYPE.NUMBER,
    keyword: "person twitter followers",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_PHONE,
    label: "Contact phone",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "contact phone",
  },
  {
    entity: ATTRIBUTE_ENTITY.PEOPLE,
    property: SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_SENIORITY,
    label: "Job position",
    attributeType: ATTRIBUTE_TYPE.STRING,
    keyword: "job position",
  },
];

export const ATTRIBUTE_TYPE_FOR_POPUP = {
  STRING: ATTRIBUTE_TYPE.STRING,
  NUMBER: ATTRIBUTE_TYPE.NUMBER,
  DATE: ATTRIBUTE_TYPE.DATE,
  SELECT: ATTRIBUTE_TYPE.SELECT,
  TEXT: "text",
} as const;
